import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  LIVE_SIGNAL_ARM_THRESHOLD_HELP,
  LIVE_SIGNAL_ARM_THRESHOLD_LABEL,
  LIVE_SIGNAL_RELEASE_TICKS_HELP,
  LIVE_SIGNAL_RELEASE_TICKS_LABEL,
  LIVE_SIGNAL_SECTION_LABEL,
  LIVE_SIGNAL_SWITCH_LABEL,
  LIVE_SIGNAL_WINDOW_MS_HELP,
  LIVE_SIGNAL_WINDOW_MS_LABEL,
  LiveSignalTuningInput,
  ParsedLiveSignalTuning
} from '../shared/liveSignalTuning';

interface Props {
  sourcesEnabled: boolean;
  onSourcesEnabledChange: (next: boolean) => void;
  tuning: LiveSignalTuningInput;
  onTuningChange: (next: LiveSignalTuningInput) => void;
  parsedTuning: ParsedLiveSignalTuning;
  /**
   * Same fail-CLOSED contract as `FeatureFlagsSection.locked`: true while the
   * group is on/panicked, OR while pod state is unknown — the backend 409s
   * an effective change to `liveSignalSources`/`liveSignalTuning` while the
   * group is running, because both are delivered to the engine pod at BOOT.
   */
  locked: boolean;
  /** Why it is locked, shown in the banner. */
  lockReason?: string;
}

const DEFAULT_LOCK_REASON =
  'Stop the group to change live-signal sources — the set is delivered to the engine pod at boot.';

/**
 * Group-level config for the External Live Signals framework: the fixed
 * source preset (on/off) plus the pressure-detector tuning. The per-match
 * Trade360 fixture binding (`Trade360FixtureSection`) is a SEPARATE section —
 * that one is never locked, since binding a fixture has no boot-time
 * coupling on the backend.
 */
export function LiveSignalsSection({
  sourcesEnabled,
  onSourcesEnabledChange,
  tuning,
  onTuningChange,
  parsedTuning,
  locked,
  lockReason
}: Props) {
  const setField = (key: keyof LiveSignalTuningInput, value: string) =>
    onTuningChange({ ...tuning, [key]: value });

  return (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 1 }}>
        {LIVE_SIGNAL_SECTION_LABEL}
      </Typography>

      {locked && (
        <Alert severity="info" sx={{ mb: 1, py: 0, fontSize: '0.72rem' }}>
          {lockReason ?? DEFAULT_LOCK_REASON}
        </Alert>
      )}

      <Tooltip title={locked ? (lockReason ?? DEFAULT_LOCK_REASON) : ''}>
        {/* MUI drops tooltips on disabled controls — the span keeps the
            explanation reachable, same trick as FeatureFlagsSection. */}
        <span>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={sourcesEnabled}
                disabled={locked}
                onChange={e => onSourcesEnabledChange(e.target.checked)}
                inputProps={{ 'aria-label': LIVE_SIGNAL_SECTION_LABEL }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.8rem' }}>
                {LIVE_SIGNAL_SWITCH_LABEL}
              </Typography>
            }
          />
        </span>
      </Tooltip>

      <Stack spacing={2} sx={{ mt: 1.5 }}>
        <TextField
          label={LIVE_SIGNAL_ARM_THRESHOLD_LABEL}
          size="small"
          type="number"
          disabled={locked}
          value={tuning.pressureArmThreshold}
          onChange={e => setField('pressureArmThreshold', e.target.value)}
          error={!!parsedTuning.errors.pressureArmThreshold}
          helperText={
            parsedTuning.errors.pressureArmThreshold ??
            LIVE_SIGNAL_ARM_THRESHOLD_HELP
          }
          fullWidth
        />
        <TextField
          label={LIVE_SIGNAL_WINDOW_MS_LABEL}
          size="small"
          type="number"
          disabled={locked}
          value={tuning.pressureWindowMs}
          onChange={e => setField('pressureWindowMs', e.target.value)}
          error={!!parsedTuning.errors.pressureWindowMs}
          helperText={
            parsedTuning.errors.pressureWindowMs ?? LIVE_SIGNAL_WINDOW_MS_HELP
          }
          fullWidth
        />
        <TextField
          label={LIVE_SIGNAL_RELEASE_TICKS_LABEL}
          size="small"
          type="number"
          disabled={locked}
          value={tuning.pressureReleaseOnRepricedTicks}
          onChange={e =>
            setField('pressureReleaseOnRepricedTicks', e.target.value)
          }
          error={!!parsedTuning.errors.pressureReleaseOnRepricedTicks}
          helperText={
            parsedTuning.errors.pressureReleaseOnRepricedTicks ??
            LIVE_SIGNAL_RELEASE_TICKS_HELP
          }
          fullWidth
        />
      </Stack>
    </Box>
  );
}

export default LiveSignalsSection;
